import { useNavigate } from "@tanstack/react-router";
import type { CreatorTriggerType, TriggerConfig } from "@unbox/api";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowSquareDown,
  ArrowSquareUp,
  ChevronDown,
  Clock,
  ClockSnooze,
  Copy01,
  CurrencyDollarCircle,
  Edit03,
  FilePlus02,
  Mail01,
  MessageTextSquare02,
  PackageCheck,
  Play,
  PlayCircle,
  Plus,
  RefreshCw01,
  Rocket01,
  Save01,
  SlashCircle01,
  Square,
  Trash02,
  TrendUp02,
  Users01,
  XClose,
} from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { useMessageTemplates } from "@/api/message-templates";
import {
  useDeleteAutomation,
  useSaveSequenceTriggerBinding,
  useSequenceImpactPreview,
  useSequencesByIds,
  useSequenceTemplates,
  useSequenceTriggerBinding,
  useSequenceTriggers,
  useUpsertSequence,
} from "@/api/sequences";
import { useDismissHero } from "@/api/user-preferences";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { PageHeroKey } from "@/components/application/page-hero/page-hero";
import {
  PolicyActionCard,
  PolicyDelayPill,
  type PolicyMarker,
  PolicyRail,
  PolicySectionDivider,
  PolicyStepRow,
} from "@/components/application/policy-sequence/policy-sequence";
import { Button } from "@/components/base/buttons/button";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { toastImageUploadFailed } from "@/components/message-composer/composer-toasts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { pluralize } from "@/utils/text";
import {
  AutomatedMessageFields,
  hasMessageContent,
  type MessageDraft,
  useCommitMessageDraft,
  useSendAtControls,
} from "./automated-message-fields";
import { AutomationScopeRail, GLOBAL } from "./automation-scope-rail";
import { CohortDot, type CohortOption, CohortSelectPopover } from "./cohort-select-panel";
import { collapseForPersist, expandPersistedSteps, type Step } from "./step-builder";

export const TRIGGER_ICON: Record<CreatorTriggerType, typeof Mail01> = {
  collaboration_invite: Mail01,
  sample_delivered: PackageCheck,
  creator_dormant: ClockSnooze,
  first_sale: CurrencyDollarCircle,
  gmv_threshold_reached: TrendUp02,
  first_collab_video: PlayCircle,
  video_viral: Rocket01,
  cohort_entered: Users01,
};

const THRESHOLD_FIELD: Partial<Record<CreatorTriggerType, { key: "amount" | "views"; label: string; unit: string }>> = {
  gmv_threshold_reached: { key: "amount", label: "GMV threshold", unit: "total GMV" },
  video_viral: { key: "views", label: "Views threshold", unit: "views" },
};

const thresholdConfigOf = (key: "amount" | "views", value: number): TriggerConfig =>
  key === "amount" ? { amount: value } : { views: value };

const thresholdValueOf = (config: TriggerConfig, key: "amount" | "views"): number | null => {
  if (!config) return null;
  if (key === "amount") return "amount" in config ? config.amount : null;
  return "views" in config ? config.views : null;
};

/** Mirrors the prototype's two-group trigger picker over the triggers the API actually binds. */
const TRIGGER_GROUPS: { label: string; types: CreatorTriggerType[] }[] = [
  { label: "Collaborations", types: ["collaboration_invite", "sample_delivered", "first_collab_video"] },
  {
    label: "Engagement",
    types: ["creator_dormant", "first_sale", "gmv_threshold_reached", "video_viral", "cohort_entered"],
  },
];

const FLAGGED_TRIGGERS: CreatorTriggerType[] = [
  "first_sale",
  "gmv_threshold_reached",
  "first_collab_video",
  "video_viral",
  "cohort_entered",
];

const TEMPLATE_ICON: Record<string, typeof Mail01> = {
  mail: Mail01,
  package: PackageCheck,
  refresh: RefreshCw01,
};

/** A number input still yields "" for a half-typed value like "-", and Number("") is 0. */
function parseThreshold(raw: string): number | null {
  if (raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * Deliberately never disabled. Adding a step locks the trigger, because the steps are validated
 * against it, but the threshold can still be retuned — and it has to be, since a step is required
 * before saving surfaces the missing-threshold error at all.
 */
function ThresholdField({
  field,
  value,
  error,
  onChange,
}: {
  field: { key: "amount" | "views"; label: string; unit: string };
  value: number | null;
  error: string | null;
  onChange: (next: number | null) => void;
}) {
  return (
    <div className="w-40 pl-11">
      <Input
        type="number"
        label={field.label}
        value={value === null ? "" : String(value)}
        onChange={(next) => onChange(parseThreshold(next))}
        isInvalid={error !== null}
        hint={error ?? undefined}
      />
    </div>
  );
}

function InUseBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand-950">
      <span aria-hidden className="size-1.5 rounded-full bg-brand-950" />
      In use
    </span>
  );
}

type Variation = {
  id: string;
  /** The sequence backing this variation, or null until it is first saved. */
  sequenceId: string | null;
  cohortIds: string[];
  steps: Step[];
  /** Stands in for the default's threshold for these cohorts. Null when the trigger takes none. */
  threshold: number | null;
};

const makeStep = (type: Step["type"], patch: Partial<Step> = {}): Step => ({
  step: 0,
  type,
  delayDays: type === "wait" ? 3 : 0,
  sendAtHour: null,
  sendAtTimezone: null,
  messageTemplateId: null,
  customMessage: null,
  imageId: null,
  imageUrl: null,
  ...patch,
});

/** Renumbers a flow so `step` stays the render key and the persisted order. */
const renumber = (steps: Step[]): Step[] => steps.map((s, i) => ({ ...s, step: i }));

const DELAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i);

/** Deterministic stringify (sorted object keys) so two equivalent states compare equal
 *  regardless of the order their fields were built in. */
const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
};

type SavedShape = {
  triggerType: CreatorTriggerType | null;
  enabled: boolean;
  globalSteps: Step[];
  variations: Pick<Variation, "cohortIds" | "steps" | "threshold">[];
  excludedCohortIds: string[];
  threshold: number | null;
};

/** Everything the dirty check compares, before it is reduced to the comparable shape. */
type FlowState = {
  triggerType: CreatorTriggerType | null;
  enabled: boolean;
  globalSteps: Step[];
  variations: Variation[];
  excludedCohortIds: string[];
  threshold: number | null;
};

/**
 * Reduces a flow to the fields that decide whether it differs from what was saved. Snapshots and
 * the check that reads them both go through here: a variation carries an id and a sequenceId that
 * are assigned during saving, and serializing those on one side only makes every comparison
 * differ.
 */
const savedShapeOf = (state: FlowState): SavedShape => ({
  triggerType: state.triggerType,
  enabled: state.enabled,
  globalSteps: state.globalSteps,
  variations: state.variations.map((v) => ({ cohortIds: v.cohortIds, steps: v.steps, threshold: v.threshold })),
  excludedCohortIds: state.excludedCohortIds,
  threshold: state.threshold,
});

export function AutomationBuilder({
  triggerParam,
  fromEmptyState,
}: {
  triggerParam: string;
  fromEmptyState?: boolean;
}) {
  const navigate = useNavigate();
  const merchantId = useMerchant();
  const dismissHero = useDismissHero();

  const isNew = triggerParam === "new";
  const boundTrigger = isNew ? null : (triggerParam as CreatorTriggerType);

  const { data: allTriggers = [], isLoading: catalogueLoading } = useSequenceTriggers();
  const milestoneTriggersEnabled = useFeatureFlag("F-automation-milestone-triggers");
  const catalogue = useMemo(
    () => (milestoneTriggersEnabled ? allTriggers : allTriggers.filter((t) => !FLAGGED_TRIGGERS.includes(t.type))),
    [allTriggers, milestoneTriggersEnabled],
  );
  const { data: binding, isLoading: bindingLoading } = useSequenceTriggerBinding(boundTrigger);
  const { data: templates = [] } = useSequenceTemplates();
  const { data: cohorts = [] } = useCreatorCohorts();
  const { data: messageTemplates = [] } = useMessageTemplates();

  const upsertSequence = useUpsertSequence();
  const saveBinding = useSaveSequenceTriggerBinding();
  const deleteAutomation = useDeleteAutomation();

  // --- local flow state -----------------------------------------------------

  const [triggerType, setTriggerType] = useState<CreatorTriggerType | null>(boundTrigger);
  const [enabled, setEnabled] = useState(true);
  const [scopeId, setScopeId] = useState<string>(GLOBAL);
  const [showStart, setShowStart] = useState(isNew);
  const [globalSequenceId, setGlobalSequenceId] = useState<string | null>(null);
  const [globalSteps, setGlobalSteps] = useState<Step[]>([]);
  const [variations, setVariations] = useState<Variation[]>([]);
  const [excludedCohortIds, setExcludedCohortIds] = useState<string[]>([]);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  /** Variations that have not yet answered "reuse steps from". */
  const [pendingCopy, setPendingCopy] = useState<string[]>([]);
  const [confirmBack, setConfirmBack] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  /** Set when the editor opened on a step it created itself, so closing it empty removes the step. */
  const [editorCreatedStep, setEditorCreatedStep] = useState(false);
  const [messageDraft, setMessageDraft] = useState<MessageDraft | null>(null);
  const { commit: commitMessageDraft, isPending: isCommittingMessage } = useCommitMessageDraft();

  // Sequences named by the binding, so each scope's steps can be hydrated in one pass.
  const boundSequenceIds = useMemo(() => {
    if (!binding) return [];
    const ids = new Set<string>();
    if (binding.defaultSequence) ids.add(binding.defaultSequence.id);
    for (const o of binding.overrides) if (o.sequence) ids.add(o.sequence.id);
    return [...ids];
  }, [binding]);

  const sequenceQueries = useSequencesByIds(boundSequenceIds);
  const sequencesLoaded = sequenceQueries.every((q) => q.isSuccess || q.isError);

  // Hydrate once the binding and every bound sequence have landed. Keyed on the trigger so
  // navigating between automations re-seeds rather than keeping the previous one's flow.
  const hydratedFor = useRef<string | null>(null);
  /** The last-saved shape, snapshotted right after hydration and after every successful save.
   *  The back button diffs against this instead of a boolean flag, so it stays correct even
   *  when an edit is undone by hand (e.g. a step added then removed). */
  const savedSnapshotRef = useRef<string | null>(null);
  const snapshotSaved = (state: FlowState) => {
    savedSnapshotRef.current = stableStringify(savedShapeOf(state));
  };

  useEffect(() => {
    if (!isNew || savedSnapshotRef.current !== null) return;
    snapshotSaved({
      triggerType: null,
      enabled: true,
      globalSteps: [],
      variations: [],
      excludedCohortIds: [],
      threshold: null,
    });
  }, [isNew]);

  useEffect(() => {
    if (isNew || !binding || !sequencesLoaded) return;
    if (hydratedFor.current === triggerParam) return;
    hydratedFor.current = triggerParam;

    const stepsFor = (sequenceId: string): Step[] => {
      const found = sequenceQueries.find((q) => q.data?.id === sequenceId)?.data;
      return renumber(expandPersistedSteps(found?.steps ?? []));
    };

    const nextTriggerType = binding.type;
    const nextEnabled = binding.enabled;
    const nextGlobalSteps = binding.defaultSequence ? stepsFor(binding.defaultSequence.id) : [];

    setTriggerType(nextTriggerType);
    setEnabled(nextEnabled);
    setGlobalSequenceId(binding.defaultSequence?.id ?? null);
    setGlobalSteps(nextGlobalSteps);

    // A variation is every cohort pointing at the same sequence, so the flat override list
    // regroups by sequence id.
    const bySequence = new Map<string, { cohortIds: string[]; triggerConfig: TriggerConfig }>();
    const excluded: string[] = [];
    for (const o of binding.overrides) {
      if (!o.cohort) continue;
      if (o.enabled && o.sequence) {
        const group = bySequence.get(o.sequence.id);
        if (group) group.cohortIds.push(o.cohort.id);
        else bySequence.set(o.sequence.id, { cohortIds: [o.cohort.id], triggerConfig: o.triggerConfig });
      } else if (!o.enabled) {
        excluded.push(o.cohort.id);
      }
    }

    const field = THRESHOLD_FIELD[nextTriggerType];
    const nextThreshold = field ? thresholdValueOf(binding.triggerConfig, field.key) : null;
    const nextVariations = [...bySequence.entries()].map(([sequenceId, group]) => ({
      id: `var-${sequenceId}`,
      sequenceId,
      cohortIds: group.cohortIds,
      steps: stepsFor(sequenceId),
      // Saving writes one config across a variation's cohorts, so any of the group carries it.
      threshold: field ? thresholdValueOf(group.triggerConfig, field.key) : null,
    }));
    setVariations(nextVariations);
    setExcludedCohortIds(excluded);
    setThreshold(nextThreshold);
    setThresholdError(null);

    snapshotSaved({
      triggerType: nextTriggerType,
      enabled: nextEnabled,
      globalSteps: nextGlobalSteps,
      variations: nextVariations,
      excludedCohortIds: excluded,
      threshold: nextThreshold,
    });
  }, [isNew, binding, sequencesLoaded, sequenceQueries, triggerParam]);

  // --- derived --------------------------------------------------------------

  const cohortById = useMemo(() => new Map(cohorts.map((c) => [c.id, c])), [cohorts]);
  const cohortOptions: CohortOption[] = useMemo(
    () => cohorts.map((c) => ({ id: c.id, name: c.name, color: c.color, assignedCount: c.assignedCount })),
    [cohorts],
  );
  const cohortNames = (ids: string[]) => ids.map((id) => cohortById.get(id)?.name ?? "Unknown cohort");
  const reachOf = (ids: string[]) => ids.reduce((n, id) => n + (cohortById.get(id)?.assignedCount ?? 0), 0);

  const activeVariation = variations.find((v) => v.id === scopeId) ?? null;
  const steps = activeVariation ? activeVariation.steps : globalSteps;
  const setSteps = (next: Step[]) => {
    if (activeVariation) {
      setVariations((prev) => prev.map((v) => (v.id === activeVariation.id ? { ...v, steps: renumber(next) } : v)));
    } else {
      setGlobalSteps(renumber(next));
    }
  };

  const setThresholdFor = (next: number | null) => {
    if (activeVariation) {
      setVariations((prev) => prev.map((v) => (v.id === activeVariation.id ? { ...v, threshold: next } : v)));
    } else {
      setThreshold(next);
    }
  };

  const claimedCohortIds = new Set([...variations.flatMap((v) => v.cohortIds), ...excludedCohortIds]);
  const baseScopeName = claimedCohortIds.size > 0 ? "All other creators" : "All Creators";
  const unclaimedCohorts = cohortOptions.filter((c) => !claimedCohortIds.has(c.id));
  const allCohortsAssigned = cohorts.length > 0 && unclaimedCohorts.length === 0;
  const scopeInertReason = !triggerType
    ? "Choose a trigger first"
    : cohorts.length === 0
      ? "Create a cohort first to customize this automation."
      : allCohortsAssigned
        ? "Every cohort is already assigned. To use it here, remove it from a variation or exclusion."
        : null;

  const { data: impact } = useSequenceImpactPreview(triggerType, excludedCohortIds);

  const definition = catalogue.find((t) => t.type === triggerType) ?? null;
  const TriggerIcon = triggerType ? TRIGGER_ICON[triggerType] : Users01;

  /** One automation per trigger: any other trigger that already carries a binding is inert. */
  const inUseBy = (type: CreatorTriggerType): string | null => {
    if (type === boundTrigger) return null;
    const entry = catalogue.find((t) => t.type === type);
    if (!entry) return null;
    if (entry.defaultSequence) return entry.label;
    return entry.cohortOverrideCount > 0 ? entry.label : null;
  };

  /**
   * A variation isn't ready to build until it has an audience and has said where its flow comes
   * from, otherwise a step lands on a copy that is about to be overwritten.
   */
  const stepsLockedReason = !activeVariation
    ? null
    : activeVariation.cohortIds.length === 0
      ? "First select a cohort for this variation"
      : pendingCopy.includes(activeVariation.id)
        ? "First choose what this automation copies from"
        : null;
  const stepsLocked = stepsLockedReason !== null;

  const editingStep = editingStepIndex !== null ? steps[editingStepIndex] : undefined;
  const sendAt = useSendAtControls({
    initialValue: { sendAtHour: editingStep?.sendAtHour ?? null, sendAtTimezone: editingStep?.sendAtTimezone ?? null },
    resetKey: editingStepIndex ?? "none",
    onChange: (value) => {
      if (editingStepIndex !== null) updateStep(editingStepIndex, value);
    },
  });

  // --- mutations ------------------------------------------------------------

  const addStep = (step: Step) => setSteps([...steps, step]);
  const updateStep = (index: number, patch: Partial<Step>) =>
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const removeStep = (index: number) => setSteps(steps.filter((_, i) => i !== index));
  const moveStep = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    setSteps(next);
  };

  const saveMessageEditor = async (index: number) => {
    if (!messageDraft) return;
    try {
      updateStep(index, await commitMessageDraft(messageDraft));
      setEditingStepIndex(null);
      setEditorCreatedStep(false);
    } catch {
      toastImageUploadFailed();
    }
  };

  const closeMessageEditor = () => {
    const index = editingStepIndex;
    if (index !== null && editorCreatedStep) {
      const step = steps[index];
      if (step && !step.customMessage && !step.messageTemplateId) removeStep(index);
    }
    setEditingStepIndex(null);
    setEditorCreatedStep(false);
  };

  const createVariation = () => {
    const id = `var-new-${variations.length}-${globalSteps.length}`;
    setVariations((prev) => [...prev, { id, sequenceId: null, cohortIds: [], steps: [], threshold }]);
    setPendingCopy((prev) => [...prev, id]);
    setScopeId(id);
  };

  const setVariationCohorts = (variationId: string, cohortIds: string[]) =>
    setVariations((prev) => prev.map((v) => (v.id === variationId ? { ...v, cohortIds } : v)));

  const removeVariation = (id: string) => {
    setVariations((prev) => prev.filter((v) => v.id !== id));
    if (scopeId === id) setScopeId(GLOBAL);
  };

  const templateName = (id: string | null) => messageTemplates.find((t) => t.id === id)?.name ?? null;

  const hasUnsavedChanges = () =>
    savedSnapshotRef.current !== null &&
    stableStringify(savedShapeOf({ triggerType, enabled, globalSteps, variations, excludedCohortIds, threshold })) !==
      savedSnapshotRef.current;

  // --- save -----------------------------------------------------------------

  const derivedName = (variation: Variation | null): string => {
    if (!variation) return definition?.label ?? "Creator outreach";
    const names = cohortNames(variation.cohortIds);
    return names.length > 0 ? names.join(", ") : "Cohort variation";
  };

  const save = async () => {
    if (!triggerType) {
      toastError("Choose a trigger first", { description: "An automation needs a trigger before it can be saved." });
      return false;
    }
    if (globalSteps.length === 0) {
      toastError("Add at least one step", { description: "An automation needs a step before it can be saved." });
      return false;
    }
    const requiredThreshold = THRESHOLD_FIELD[triggerType];
    if (requiredThreshold) {
      const message = `Enter a ${requiredThreshold.label.toLowerCase()} above zero.`;
      const missing = (value: number | null) => value === null || value <= 0;
      // The field only exists on the scope it belongs to, so switch to whichever one is short.
      const offending = variations.find((v) => v.cohortIds.length > 0 && missing(v.threshold));
      if (missing(threshold) || offending) {
        setScopeId(missing(threshold) ? GLOBAL : (offending?.id ?? GLOBAL));
        setThresholdError(message);
        return false;
      }
    }

    // Changing the trigger of a saved automation moves it: the new binding is written first and
    // the old one dropped after, so a failure part-way leaves the automation on two triggers —
    // visible in the list, and fixable there — rather than on none.
    const movedFrom = boundTrigger && boundTrigger !== triggerType ? boundTrigger : null;

    try {
      // Sequences first, binding last: a failure part-way leaves unreferenced sequences rather
      // than a binding pointing at a sequence that was never written.
      const defaultSequenceId = await upsertSequence.mutateAsync({
        sequenceId: globalSequenceId,
        name: derivedName(null),
        steps: collapseForPersist(globalSteps),
      });
      setGlobalSequenceId(defaultSequenceId);

      const thresholdField = THRESHOLD_FIELD[triggerType];
      const configOf = (value: number | null): TriggerConfig =>
        thresholdField && value !== null ? thresholdConfigOf(thresholdField.key, value) : null;

      const saved: { cohortIds: string[]; sequenceId: string; triggerConfig: TriggerConfig }[] = [];
      for (const variation of variations) {
        if (variation.cohortIds.length === 0) continue;
        const sequenceId = await upsertSequence.mutateAsync({
          sequenceId: variation.sequenceId,
          name: derivedName(variation),
          steps: collapseForPersist(variation.steps),
        });
        saved.push({ cohortIds: variation.cohortIds, sequenceId, triggerConfig: configOf(variation.threshold) });
      }
      setVariations((prev) =>
        prev.map((v) => {
          const match = saved.find((s) => s.cohortIds.join() === v.cohortIds.join());
          return match ? { ...v, sequenceId: match.sequenceId } : v;
        }),
      );

      await saveBinding.mutateAsync({
        triggerType,
        defaultSequenceId,
        enabled,
        triggerConfig: configOf(threshold),
        overrides: [
          ...saved.flatMap(({ cohortIds, sequenceId, triggerConfig }) =>
            cohortIds.map((cohortId) => ({ cohortId, enabled: true, sequenceId, triggerConfig })),
          ),
          ...excludedCohortIds.map((cohortId) => ({
            cohortId,
            enabled: false,
            sequenceId: null,
            triggerConfig: null,
          })),
        ],
        ...(movedFrom && { movedFrom }),
      });

      if (movedFrom) await deleteAutomation.mutateAsync(movedFrom);

      snapshotSaved({ triggerType, enabled, globalSteps, variations, excludedCohortIds, threshold });

      toastSuccess("Automation saved", {
        description: enabled
          ? "It's live and will run for creators who match your trigger."
          : "It stays disabled until you enable it.",
      });
      if (fromEmptyState) dismissHero.mutate(PageHeroKey.AutomateOutreach);
      if (isNew || movedFrom) {
        // The URL moves onto the trigger now bound, which the hydration effect keys on.
        // What is on screen is what was just written, so claim the destination as hydrated;
        // otherwise it re-seeds from the server and the flow appears to reload itself.
        hydratedFor.current = triggerType;
        navigate({
          to: "/merchant/$merchantId/automate-outreach/automation/$triggerType",
          params: { merchantId, triggerType },
        });
      }
      return true;
    } catch (e) {
      toastError("Couldn't save automation", { description: e instanceof Error ? e.message : undefined });
      return false;
    }
  };

  const goBack = () => navigate({ to: "/merchant/$merchantId/automate-outreach", params: { merchantId } });

  // --- render ---------------------------------------------------------------

  if (catalogueLoading || (!isNew && (bindingLoading || !sequencesLoaded))) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-unbox-cream-25">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center h-screen overflow-y-auto bg-unbox-cream-25">
      <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-10 px-16 pt-16 pb-16")}>
        <div className="flex shrink-0 items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => (hasUnsavedChanges() ? setConfirmBack(true) : goBack())}
            className="inline-flex items-center gap-1 text-sm font-semibold text-tertiary transition-colors duration-100 hover:text-primary"
          >
            <ArrowLeft className="size-5" aria-hidden />
            Back to automations
          </button>

          {!showStart && (
            <div className="flex shrink-0 items-center gap-5">
              <Toggle
                label={enabled ? "Automation Enabled" : "Automation Disabled"}
                isSelected={enabled}
                onChange={setEnabled}
              />
              <Button
                iconLeading={<Save01 className="size-5" />}
                isLoading={upsertSequence.isPending || saveBinding.isPending}
                onClick={save}
              >
                Save automation
              </Button>
            </div>
          )}
        </div>

        {!showStart && !enabled && (
          <div className="flex shrink-0 items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 px-5 py-4">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning-700" aria-hidden />
            <p className="text-sm leading-5 font-semibold text-warning-800">
              This automation will be disabled when you save. While disabled, it won&apos;t apply to any creators.
            </p>
          </div>
        )}

        <div className="flex shrink-0 flex-row-reverse items-stretch gap-6">
          {/* Applies to */}
          {!showStart && (
            <AutomationScopeRail
              merchantId={merchantId}
              inertReason={scopeInertReason}
              scopeId={scopeId}
              onSelectScope={(id) => {
                setScopeId(id);
                // The threshold error belongs to the scope that raised it.
                setThresholdError(null);
              }}
              baseScopeName={baseScopeName}
              baseReach={impact?.matchCount ?? 0}
              cohortCount={cohorts.length}
              cohortOptions={cohortOptions}
              cohortNames={cohortNames}
              reachOf={reachOf}
              variations={variations}
              excludedCohortIds={excludedCohortIds}
              claimedCohortIds={claimedCohortIds}
              onCreateVariation={createVariation}
              onChangeVariationCohorts={setVariationCohorts}
              onRemoveVariation={removeVariation}
              onChangeExcluded={setExcludedCohortIds}
            />
          )}

          {/* Automation canvas */}
          <section className="relative flex min-w-0 flex-1 flex-col gap-8 rounded-xl bg-unbox-cream-50 p-8">
            {showStart ? (
              <div className="flex flex-col gap-8">
                <section className="flex flex-col gap-3">
                  <PolicySectionDivider label="Start from scratch" />
                  <button
                    type="button"
                    onClick={() => setShowStart(false)}
                    className="flex w-full flex-col items-start gap-3 rounded-lg border border-secondary bg-primary p-5 text-left transition-all duration-100 hover:border-gray-300 hover:shadow-xs"
                  >
                    <Plus aria-hidden className="size-6 text-brand-950" />
                    <span className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-primary">Build your own</span>
                      <span className="text-[13px] text-tertiary">Choose a trigger and add the steps yourself.</span>
                    </span>
                  </button>
                </section>

                <section className="flex flex-col gap-3">
                  <PolicySectionDivider label="Use a template" />
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {templates.map((template) => {
                      const Icon = TEMPLATE_ICON[template.iconKey] ?? Mail01;
                      const disabledBy = inUseBy(template.triggerType);
                      return (
                        <button
                          key={template.id}
                          type="button"
                          disabled={!!disabledBy}
                          onClick={() => {
                            setTriggerType(template.triggerType);
                            setGlobalSteps(
                              renumber(
                                template.steps.flatMap((s) => [
                                  makeStep("wait", { delayDays: s.delayDays }),
                                  makeStep("send_message", { customMessage: s.customMessage }),
                                ]),
                              ),
                            );
                            setShowStart(false);
                          }}
                          className={cx(
                            "flex flex-col items-start gap-3 rounded-lg border p-5 text-left transition-all duration-100",
                            disabledBy
                              ? "cursor-not-allowed border-transparent bg-unbox-cream-50"
                              : "border-secondary bg-primary hover:border-gray-300 hover:shadow-xs",
                          )}
                        >
                          <Icon aria-hidden className="size-6 text-brand-950" />
                          <span className="flex flex-1 flex-col gap-1">
                            <span className="text-sm font-semibold text-primary">{template.name}</span>
                            <span className="text-[13px] text-tertiary">{template.description}</span>
                          </span>
                          <span className="flex w-full items-center justify-between gap-2 text-xs font-medium text-quaternary">
                            {disabledBy
                              ? `In use by ${disabledBy}`
                              : `Template · ${pluralize(template.steps.length, "step", "steps")}`}
                            {disabledBy && <InUseBadge />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              </div>
            ) : (
              <div key={scopeId} className="flex flex-col gap-10">
                {/* Which audience this flow is being edited for. */}
                <div className="-mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-secondary pb-4">
                  <div className="flex items-center gap-2">
                    <TriggerIcon className="size-5 shrink-0 text-tertiary" aria-hidden />
                    <p className="text-lg leading-7 text-tertiary">
                      Customizing{" "}
                      <span className="font-semibold text-primary">{definition?.label ?? "Automation"}</span>
                      {!activeVariation && (
                        <>
                          {" "}
                          for <span className="font-semibold text-primary">{baseScopeName}</span>
                        </>
                      )}
                    </p>
                  </div>

                  {activeVariation && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-lg leading-7 text-tertiary">For</span>
                      {activeVariation.cohortIds.map((id) => (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 py-1 pr-1.5 pl-3 text-sm leading-5 font-medium text-brand-800"
                        >
                          <CohortDot color={cohortById.get(id)?.color ?? "#64748b"} />
                          {cohortById.get(id)?.name ?? "Unknown cohort"}
                          <button
                            type="button"
                            aria-label={`Remove ${cohortById.get(id)?.name ?? "cohort"}`}
                            onClick={() =>
                              setVariationCohorts(
                                activeVariation.id,
                                activeVariation.cohortIds.filter((c) => c !== id),
                              )
                            }
                            className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-brand-700 transition-colors duration-100 hover:bg-brand-100 hover:text-brand-900"
                          >
                            <XClose className="size-[13px]" aria-hidden />
                          </button>
                        </span>
                      ))}

                      <CohortSelectPopover
                        trigger={
                          activeVariation.cohortIds.length > 0 && unclaimedCohorts.length === 0 ? (
                            <Dropdown.DotsButton aria-label="Change cohorts for this variation" />
                          ) : (
                            <button
                              type="button"
                              className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-gray-950 py-1 pr-3 pl-2.5 text-[13px] leading-5 font-semibold text-white transition-colors duration-100 hover:bg-gray-800"
                            >
                              <Plus className="size-3.5" aria-hidden />
                              {activeVariation.cohortIds.length === 0 ? "Select cohort" : "Add cohorts"}
                            </button>
                          )
                        }
                        options={cohortOptions.filter(
                          (c) => activeVariation.cohortIds.includes(c.id) || !claimedCohortIds.has(c.id),
                        )}
                        initialSelected={activeVariation.cohortIds}
                        onConfirm={(ids) => setVariationCohorts(activeVariation.id, ids)}
                      />
                    </div>
                  )}
                </div>

                {/* Trigger */}
                <section className="flex flex-col gap-3 border-b border-secondary pb-6">
                  <PolicySectionDivider label="Trigger" size="md" rule={false} />

                  {!triggerType ? (
                    <div className="flex flex-col gap-5 pt-2">
                      {TRIGGER_GROUPS.map((group) => (
                        <div key={group.label} className="flex flex-col gap-3">
                          <div className="flex items-center gap-4">
                            <span className="shrink-0 text-sm font-medium text-brand-700">{group.label}</span>
                            <div className="h-px flex-1 bg-gray-200" />
                          </div>
                          <div className="grid gap-3 lg:grid-cols-2">
                            {group.types.map((type) => {
                              const entry = catalogue.find((t) => t.type === type);
                              if (!entry) return null;
                              const Icon = TRIGGER_ICON[type];
                              const claimed = inUseBy(type);
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  disabled={!!claimed}
                                  onClick={() => setTriggerType(type)}
                                  className={cx(
                                    "flex items-start gap-4 rounded-md border p-4 text-left transition-colors duration-100",
                                    claimed
                                      ? "cursor-not-allowed border-transparent bg-unbox-cream-50"
                                      : "border-secondary bg-primary hover:border-gray-300 hover:shadow-xs",
                                  )}
                                >
                                  <Icon
                                    aria-hidden
                                    className={cx(
                                      "mt-0.5 size-5 shrink-0",
                                      claimed ? "text-gray-400" : "text-secondary",
                                    )}
                                  />
                                  <span className="flex min-w-0 flex-col gap-0.5">
                                    <span
                                      className={cx(
                                        "text-sm font-semibold",
                                        claimed ? "text-tertiary" : "text-primary",
                                      )}
                                    >
                                      {entry.label}
                                    </span>
                                    <span className="text-[13px] text-tertiary">
                                      {claimed ? `In use by ${claimed}` : entry.description}
                                    </span>
                                  </span>
                                  {claimed && (
                                    <span className="ml-auto">
                                      <InUseBadge />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <PolicyStepRow marker={{ type: "icon", icon: <Play /> }}>
                      <PolicyActionCard
                        surface="white"
                        icon={<TriggerIcon />}
                        title={definition?.label ?? triggerType}
                        description={definition?.description}
                        actions={
                          !activeVariation ? (
                            <Button
                              color="secondary"
                              size="sm"
                              iconLeading={<RefreshCw01 className="size-4" />}
                              onClick={() => setTriggerType(null)}
                            >
                              Change
                            </Button>
                          ) : undefined
                        }
                      />
                    </PolicyStepRow>
                  )}

                  {triggerType && THRESHOLD_FIELD[triggerType] && (
                    <ThresholdField
                      field={THRESHOLD_FIELD[triggerType]}
                      value={activeVariation ? activeVariation.threshold : threshold}
                      error={thresholdError}
                      onChange={(next) => {
                        setThresholdFor(next);
                        setThresholdError(null);
                      }}
                    />
                  )}
                </section>

                {/* Reuse steps from another scope */}
                {activeVariation && pendingCopy.includes(activeVariation.id) && triggerType && (
                  <section
                    className={cx(
                      "flex w-full flex-col gap-3",
                      activeVariation.cohortIds.length === 0 && "pointer-events-none opacity-50",
                    )}
                  >
                    <div className="flex flex-col gap-2">
                      <PolicySectionDivider label="Reuse steps from another automation?" size="md" rule={false} />
                      <p className="text-sm text-tertiary">
                        Start blank, or bring across the steps from an automation you have already built.
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      {[
                        {
                          id: "scratch",
                          label: "Start with an empty flow",
                          hint: "Build the steps yourself",
                          steps: [],
                        },
                        { id: GLOBAL, label: baseScopeName, hint: "Copy the base automation", steps: globalSteps },
                        ...variations
                          .filter((v) => v.id !== activeVariation.id && v.cohortIds.length > 0)
                          .map((v) => ({
                            id: v.id,
                            label: cohortNames(v.cohortIds).join(", "),
                            hint: "Copy this variation",
                            steps: v.steps,
                          })),
                      ].map((source) => (
                        <button
                          key={source.id}
                          type="button"
                          onClick={() => {
                            setVariations((prev) =>
                              prev.map((v) =>
                                v.id === activeVariation.id ? { ...v, steps: renumber([...source.steps]) } : v,
                              ),
                            );
                            setPendingCopy((prev) => prev.filter((id) => id !== activeVariation.id));
                          }}
                          className="flex cursor-pointer items-center gap-3 rounded-lg border border-secondary bg-primary px-4 py-3.5 text-left transition-all duration-100 hover:border-gray-300 hover:shadow-xs"
                        >
                          <span className="shrink-0 text-tertiary">
                            {source.id === "scratch" ? (
                              <FilePlus02 className="size-5" aria-hidden />
                            ) : (
                              <Copy01 className="size-5" aria-hidden />
                            )}
                          </span>
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-sm leading-5 font-semibold text-primary">
                              {source.label}
                            </span>
                            <span className="truncate text-[13px] leading-5 text-tertiary">{source.hint}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {/* Start Automation */}
                <section
                  className={cx(
                    "flex flex-col gap-3",
                    (!triggerType || stepsLocked) && "pointer-events-none opacity-50",
                  )}
                >
                  <PolicySectionDivider label="Start Automation" size="md" rule={false} />
                  <PolicyRail railed={steps.length > 0}>
                    {steps.map((step, index) => {
                      const ordinal = steps.slice(0, index + 1).filter((s) => s.type !== "wait").length;
                      const marker: PolicyMarker =
                        step.type === "wait" ? { type: "muted", icon: <Clock /> } : { type: "step", number: ordinal };

                      if (step.type === "wait") {
                        return (
                          <PolicyStepRow key={step.step} marker={marker}>
                            <PolicyDelayPill
                              surface="white"
                              amount={
                                // The select is stretched transparent over the whole chip, padding
                                // included, so the amount and its chevron are one target rather
                                // than a few pixels of digit.
                                <span className="relative inline-flex h-full items-center gap-0.5 rounded-full focus-within:ring-2 focus-within:ring-brand/60">
                                  <select
                                    aria-label="Delay in business days"
                                    value={step.delayDays}
                                    onChange={(e) => updateStep(index, { delayDays: Number(e.target.value) })}
                                    className="absolute inset-y-0 -inset-x-1 cursor-pointer appearance-none opacity-0"
                                  >
                                    {DELAY_OPTIONS.map((d) => (
                                      <option key={d} value={d}>
                                        {d}
                                      </option>
                                    ))}
                                  </select>
                                  <span className="px-0.5 text-xs font-bold text-primary">{step.delayDays}</span>
                                  <ChevronDown aria-hidden className="size-3 shrink-0 text-tertiary" />
                                </span>
                              }
                              unit="business days"
                              actions={
                                <div className="flex items-center gap-0.5">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Dropdown.DotsButton aria-label="Delay step actions" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem disabled={index === 0} onSelect={() => moveStep(index, -1)}>
                                        <ArrowSquareUp className="mr-2 size-4" />
                                        Move step up
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={index === steps.length - 1}
                                        onSelect={() => moveStep(index, 1)}
                                      >
                                        <ArrowSquareDown className="mr-2 size-4" />
                                        Move step down
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-error-primary"
                                        onSelect={() => removeStep(index)}
                                      >
                                        <Trash02 className="mr-2 size-4" />
                                        Remove step
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              }
                            />
                          </PolicyStepRow>
                        );
                      }

                      if (step.type === "send_message") {
                        if (editingStepIndex === index) {
                          return (
                            <PolicyStepRow key={step.step} marker={marker} stacked>
                              <div className="flex w-full flex-col gap-3 rounded-lg border border-secondary bg-white p-4 motion-safe:animate-rise-in">
                                <div className="flex items-center justify-between gap-2">
                                  <h4 className="text-sm font-semibold text-primary">Automated message</h4>
                                  <button
                                    type="button"
                                    aria-label="Close message editor"
                                    onClick={closeMessageEditor}
                                    className="flex size-7 cursor-pointer items-center justify-center rounded-md text-fg-quaternary transition-colors duration-100 hover:bg-primary_hover hover:text-fg-secondary"
                                  >
                                    <XClose className="size-[18px]" />
                                  </button>
                                </div>

                                <AutomatedMessageFields
                                  resetKey={index}
                                  variableContext="creator-profile"
                                  autoFocus
                                  initialValue={{
                                    messageTemplateId: step.messageTemplateId,
                                    customMessage: step.customMessage,
                                    imageId: step.imageId,
                                    imageUrl: step.imageUrl,
                                  }}
                                  onChange={setMessageDraft}
                                  primaryAction={{
                                    label: "Save",
                                    onClick: () => saveMessageEditor(index),
                                    isDisabled: !messageDraft || !hasMessageContent(messageDraft),
                                    isLoading: isCommittingMessage,
                                  }}
                                >
                                  {sendAt.toggle}
                                  {sendAt.selects}
                                </AutomatedMessageFields>
                              </div>
                            </PolicyStepRow>
                          );
                        }

                        const body = step.customMessage ?? templateName(step.messageTemplateId);
                        return (
                          <PolicyStepRow key={step.step} marker={marker} stacked={!!body}>
                            <PolicyActionCard
                              surface="white"
                              icon={<MessageTextSquare02 />}
                              title="Send Automated Message"
                              body={
                                body
                                  ? {
                                      label: step.customMessage ? "Message" : "Template",
                                      text: (
                                        <span className="flex items-start gap-3">
                                          {step.imageUrl && (
                                            <img
                                              src={step.imageUrl}
                                              alt="Attached image"
                                              className="size-12 shrink-0 rounded-md border border-secondary bg-primary object-cover"
                                            />
                                          )}
                                          <span className="min-w-0 flex-1">{body}</span>
                                        </span>
                                      ),
                                    }
                                  : { label: "Message", text: "No message set yet" }
                              }
                              actions={
                                <div className="flex items-center gap-0.5">
                                  <button
                                    type="button"
                                    aria-label="Edit message"
                                    onClick={() => {
                                      setEditingStepIndex(index);
                                      setEditorCreatedStep(false);
                                    }}
                                    className="flex size-6 cursor-pointer items-center justify-center rounded p-1.5 text-tertiary hover:bg-secondary"
                                  >
                                    <Edit03 className="size-4" />
                                  </button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Dropdown.DotsButton aria-label="Message step actions" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem disabled={index === 0} onSelect={() => moveStep(index, -1)}>
                                        <ArrowSquareUp className="mr-2 size-4" />
                                        Move step up
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={index === steps.length - 1}
                                        onSelect={() => moveStep(index, 1)}
                                      >
                                        <ArrowSquareDown className="mr-2 size-4" />
                                        Move step down
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-error-primary"
                                        onSelect={() => removeStep(index)}
                                      >
                                        <Trash02 className="mr-2 size-4" />
                                        Remove step
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              }
                            />
                          </PolicyStepRow>
                        );
                      }

                      return (
                        <PolicyStepRow key={step.step} marker={marker}>
                          <PolicyActionCard
                            surface="white"
                            icon={<SlashCircle01 />}
                            title="Remove from Collaboration"
                            description="Removes the creator from the linked collaboration"
                            actions={
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Dropdown.DotsButton aria-label="Remove step actions" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem disabled={index === 0} onSelect={() => moveStep(index, -1)}>
                                    <ArrowSquareUp className="mr-2 size-4" />
                                    Move step up
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    disabled={index === steps.length - 1}
                                    onSelect={() => moveStep(index, 1)}
                                  >
                                    <ArrowSquareDown className="mr-2 size-4" />
                                    Move step down
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-error-primary" onSelect={() => removeStep(index)}>
                                    <Trash02 className="mr-2 size-4" />
                                    Remove step
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            }
                          />
                        </PolicyStepRow>
                      );
                    })}

                    <PolicyStepRow>
                      <div className={steps.length === 0 ? "w-full" : "flex w-full justify-center"}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            {steps.length === 0 ? (
                              <button
                                type="button"
                                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-gray-950 px-4 py-3.5 text-sm font-semibold text-white transition-colors duration-100 hover:bg-gray-800"
                              >
                                <Plus className="size-5" aria-hidden />
                                Add step
                              </button>
                            ) : (
                              <button
                                type="button"
                                aria-label="Add step"
                                className="inline-flex size-10 cursor-pointer items-center justify-center rounded-full bg-gray-950 text-white transition-colors duration-100 hover:bg-gray-800"
                              >
                                <Plus className="size-5" aria-hidden />
                              </button>
                            )}
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="center">
                            <DropdownMenuItem onSelect={() => addStep(makeStep("wait"))}>
                              <Clock className="mr-2 size-4" />
                              Add time Delay
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => {
                                setEditingStepIndex(steps.length);
                                setEditorCreatedStep(true);
                                addStep(makeStep("send_message"));
                              }}
                            >
                              <MessageTextSquare02 className="mr-2 size-4" />
                              Send Automated Message
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => addStep(makeStep("remove_from_collab"))}>
                              <SlashCircle01 className="mr-2 size-4" />
                              Remove from Collaboration
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </PolicyStepRow>
                  </PolicyRail>
                </section>

                {/* End Automation */}
                {triggerType && (
                  <section className={cx("flex flex-col gap-3", stepsLocked && "pointer-events-none opacity-50")}>
                    <PolicySectionDivider label="End Automation" size="md" rule={false} />
                    <PolicyRail railed={false}>
                      <PolicyStepRow marker={{ type: "icon", icon: <Square /> }}>
                        <PolicyActionCard title="Automation complete" size="md" surface="white" />
                      </PolicyStepRow>
                    </PolicyRail>
                  </section>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <Dialog open={confirmBack} onOpenChange={setConfirmBack}>
        <DialogContent className="sm:max-w-[480px]" hideCloseButton>
          <DialogHeader className="items-center space-y-2 text-center sm:text-center">
            <AlertTriangle className="mb-2 size-7 text-utility-warning-500" />
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription className="text-md">
              Your changes will be permanently lost if you don't save them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
            <Button
              color="link-gray"
              size="sm"
              onClick={() => {
                setConfirmBack(false);
                goBack();
              }}
            >
              Discard Changes
            </Button>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
              <Button color="secondary" size="sm" onClick={() => setConfirmBack(false)}>
                Keep Editing
              </Button>
              <Button
                color="primary"
                size="sm"
                isLoading={upsertSequence.isPending || saveBinding.isPending}
                onClick={async () => {
                  if (await save()) {
                    setConfirmBack(false);
                    goBack();
                  }
                }}
              >
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
